// Loading and error states for a page's data.
//
// Replaces two things Next gave for free: `loading.tsx` (the isPending branch)
// and the fact that a server component simply threw on failure. In a client app
// a failed fetch has to say so, and offer a way out.

import type { ReactNode } from "react";
import { ApiError } from "@/api/client";

/** The shimmer placeholder — reuses the `.esqueleto` class from globals.css. */
export function Esqueleto({ className = "" }: { className?: string }) {
  return <div className={`esqueleto rounded-2xl ${className}`} aria-hidden />;
}

/** A page-sized loading state, matching the old loading.tsx files. */
export function ACarregar({ linhas = 3 }: { linhas?: number }) {
  return (
    <div className="mx-auto max-w-2xl px-6 py-16" role="status" aria-live="polite">
      <span className="sr-only">A carregar…</span>
      <Esqueleto className="mx-auto h-6 w-40" />
      <Esqueleto className="mx-auto mt-4 h-10 w-72" />
      <div className="mt-10 space-y-6">
        {Array.from({ length: linhas }).map((_, i) => (
          <Esqueleto key={i} className="h-32 w-full" />
        ))}
      </div>
    </div>
  );
}

/**
 * Something went wrong fetching.
 *
 * The API's messages are already Portuguese sentences meant for a person, so
 * they're shown as-is; anything else gets a generic line rather than leaking
 * "TypeError: Failed to fetch".
 */
export function ErroConsulta({ erro, aoTentar }: { erro: unknown; aoTentar?: () => void }) {
  const mensagem =
    erro instanceof ApiError ? erro.message : "Não foi possível carregar. Tenta novamente.";

  return (
    <div className="mx-auto max-w-2xl px-6 py-16 text-center">
      <p className="text-tinta-700" role="alert">
        {mensagem}
      </p>
      {aoTentar && (
        <button
          type="button"
          onClick={aoTentar}
          className="mt-6 rounded-full bg-ouro px-6 py-3 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600"
        >
          Tentar outra vez
        </button>
      )}
    </div>
  );
}

interface ConsultaProps<T> {
  query: {
    data: T | undefined;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
  };
  linhas?: number;
  children: (data: T) => ReactNode;
}

/**
 * The loading/error/ready ladder every page repeats.
 *
 * Keeps each page reading like the server component it replaced: the body is
 * the "we have data" case, with the other two handled once, here.
 */
export function Consulta<T>({ query, linhas, children }: ConsultaProps<T>) {
  if (query.isPending) return <ACarregar linhas={linhas} />;
  if (query.isError) return <ErroConsulta erro={query.error} aoTentar={query.refetch} />;
  return <>{children(query.data as T)}</>;
}
